<template>
  <div class="patient-exercises--wrap">
    <div class="patient-exercises">
      <v-overlay v-if="sequenceLoading" :value="sequenceLoading" z-index="4">
        <v-progress-circular indeterminate size="64" />
      </v-overlay>
      <preview-zoom-dialog
        :dialogType="dialogType"
        :imageSrc="imageSrc"
        :dialogFile="dialogFile"
        :dialogFileMlUrl="dialogFileMlUrl"
        :name="imageName"
        @close-dialog="closeMediaDialog"
      />
      <v-container>
        <v-row class="fill-height">
          <v-col>
            <calendar
              :selected-date="selectedDate"
              :mark-weeks="true"
              :dot-marked-dates="exercisesCalendar"
              @selectDate="selectDate"
            />
            <v-sheet class="patient-exercises__v-sheet">
              <div class="navbar">
                <v-btn
                  class="template-button"
                  color="accent"
                  rounded
                  elevation="0"
                  @click="showSequencesDialog = true"
                  >{{ tenantTexts.participantRoutines.browseAndApplyButton }}
                  <v-icon>bhi-calendar</v-icon>
                </v-btn>
                <v-btn icon text color="accent" elevation="0" @click="prevWeek"
                  ><v-icon>bhi-arrow-left</v-icon></v-btn
                >
                <v-btn icon text color="accent" elevation="0" @click="nextWeek"
                  ><v-icon>bhi-arrow-right</v-icon></v-btn
                >
                <v-spacer />
                <v-fade-transition>
                  <div
                    v-if="quickEditProgress"
                    class="patient-exercises__quick-edit-progress"
                  >
                    <v-fade-transition>
                      <span class="mr-4" v-if="quickEditProgressValue > 100"
                        >Saving...</span
                      >
                    </v-fade-transition>
                    <v-progress-circular
                      color="accent"
                      :indeterminate="
                        quickEditProgressValue > 100 ? true : false
                      "
                      :value="quickEditProgressValue"
                    ></v-progress-circular>
                  </div>
                </v-fade-transition>
                <v-btn
                  class="ml-4"
                  small
                  elevation="0"
                  color="accent"
                  @click="toggleColumnLayout"
                >
                  <v-icon>bhi-curved-refresh</v-icon>
                </v-btn>
              </div>
              <participant-exercises-container
                ref="participantExercisesContainer"
                :selected-exercises.sync="selectedExercises"
                :days="exercises.days"
                :startDate="weekStart"
                :endDate="weekEnd"
                :columnLayoutActive="columnLayoutActive"
                :pasting="
                  !!(
                    copiedExercises.length > 0 ||
                    copiedExercise ||
                    copiedExerciseDay
                  )
                "
                :copied-exercise="copiedExercise"
                :copied-exercises="copiedExercises"
                :selected-paste="selectedPasteDays"
                :copied-exercise-day="copiedExerciseDay"
                @drag-start="onDragExerciseStart"
                @drag-end="onDragExerciseEnd"
                @change="loadExercises"
                @new-exercise="openNewExerciseDialog"
                @remove-all-exercises="openRemoveAllExercisesDialog"
                @edit-exercise="openEditExerciseDialog"
                @copy-sequence-exercise="copySequenceExercise"
                @move-exercise="prepareMoveExercise"
                @remove-exercise="prepareRemoveExercise"
                @duplicate-exercise="prepareDuplicateExercise"
                @copy-exercise-day="prepareCopyExerciseDay"
                @paste-exercise-day="preparePasteExerciseDay"
                @copy-exercise="prepareCopyExercise"
                @paste-exercise="preparePasteExercise"
                @quick-edit="prepareQuickEdit"
                @series-quick-edit="prepareSeriesQuickEdit"
                @select-paste-day="selectPasteDay"
              />
              <v-sheet
                v-if="false && isPrevWeekAllowed"
                :class="dragWeekAreaOpen ? 'visible' : ''"
                @mouseenter="prevWeekT"
                @mouseleave="
                  dragWeekHover = false
                  resetDragTimers()
                "
                class="drag-over left"
              >
                <v-progress-circular
                  color="accent"
                  :value="dragWeekHoverTimerLeft"
                  :class="dragWeekHoverTimerReset ? 'reset' : ''"
                >
                  <v-icon>bhi-arrow-left</v-icon>
                </v-progress-circular>
              </v-sheet>
              <v-sheet
                v-if="false && isNextWeekAllowed"
                :class="dragWeekAreaOpen ? 'visible' : ''"
                @mouseenter="nextWeekT"
                @mouseleave="
                  dragWeekHover = false
                  resetDragTimers()
                "
                class="drag-over right"
              >
                <v-progress-circular
                  color="accent"
                  :value="dragWeekHoverTimerRight"
                  :class="dragWeekHoverTimerReset ? 'reset' : ''"
                >
                  <v-icon>bhi-arrow-right</v-icon>
                </v-progress-circular>
              </v-sheet>
            </v-sheet>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <sequence-exercise-dialog
      :show-dialog="showNewExerciseDialog"
      :key="exerciseDialogKey"
      :exerciseDefinitionsLoading="sequenceLoading"
      :day="exerciseDay"
      :enabled-notes="true"
      :get-resource="getResource"
      :get-mime-types="getMimeTypes"
      :exercise-resource="exerciseResource ? exerciseResource : false"
      :resource="resource ? resource : false"
      :exercise-definitions="exerciseDefinitions"
      :exercise-definitions-pagination="sequenceDefinitionsPagination"
      @close-exercise-dialog="
        closeDialog()
        exerciseDialogKey++
      "
      @on-submit="prepareNewExercise"
      @on-choose-existing="addExistingExercise"
      @reload-resource="reloadExerciseResource"
      @reload-exercise-resource="reloadExerciseResource"
      @clear-resource="clearExerciseResource"
      @show-bigger-image="showBiggerImage"
      @reload-exercises="reloadExerciseDefinitions"
      @search-clear="searchClear"
      @search-on:input="searchInput"
      @search-on:multiple="searchMultiple"
      @search-on:run="searchRun"
      @show-create-new-exercise-dialog="openCreateNewExerciseDialog"
      @preview-exercise-definition="exerciseGetPreview"
    />
    <exercise-definition-dialog
      :show-dialog="showEditExerciseDialog"
      :key="newExerciseDialogKey"
      :edited-exercise="exercise"
      :add-alternatives="false"
      :enabled-notes="true"
      :participants-exercises="true"
      :get-resource="getResource"
      :get-mime-types="getMimeTypes"
      :exercise-resource="exerciseResource ? exerciseResource : false"
      :exercise-resource-additional="
        exerciseResourceAdditional ? exerciseResourceAdditional : false
      "
      :exercise-definitions="exerciseSingleDefinitions"
      :add-to-series="exercise ? false : true"
      :parameters-enabled="true"
      :repetition-options-enabled="exercise ? false : true"
      :wait-for-submit-response="true"
      :hide-prev="true"
      @close-exercise-dialog="
        showEditExerciseDialog = false
        newExerciseDialogKey++
      "
      @show-bigger-image="showBiggerImage"
      @on-edit="prepareEditExercise"
      @on-submit="createNewExerciseDefinition"
      @reload-resource="reloadExerciseResource"
      @reload-exercise-resource="reloadExerciseResource"
      @reload-exercise-resource-additional="
        reloadExerciseResource($event, 'additionalMedia')
      "
      @clear-resource="clearExerciseResource"
      @clear-resource-additional="clearExerciseResourceAdditional"
      @reload-exercises="reloadExerciseDefinitions"
      @on-series-search="loadDialogExerciseDefinitions"
    />
    <sequence-participant-apply-dialog
      :show-dialog="showSequencesDialog"
      :key="sequenceDialogKey"
      :sequence-definitions="sequenceDefinitions"
      :sequence-definitions-pagination="sequenceDefinitionsPagination"
      @reload-sequences="reloadSequenceDefinitions"
      @on-submit="applySequence"
      @close-sequence-dialog="
        closeDialog()
        sequenceDialogKey++
      "
    />
    <preview-exercise-dialog
      :show-dialog="showExercisePreviewDialog"
      @close-dialog="showExercisePreviewDialog = false"
      @preview-media="openMediaPreview"
      :exercise="exerciseDefinition"
    />
    <v-dialog
      v-model="removePrompt"
      persistent
      max-width="400"
      @click:outside="onClickOutside"
      @keydown="onKeydown"
    >
      <v-card>
        <v-card-title class="headline">Remove recurring action?</v-card-title>
        <v-card-text>
          <v-radio-group v-model="removePromptOption">
            <v-radio label="This event" value="none" />
            <v-radio label="All actions" value="all" />
            <v-radio label="This and following actions" value="following" />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="rounded-pill" text @click="closeRemovePrompt"
            >Cancel</v-btn
          >
          <v-btn
            class="rounded-pill elevation-0"
            color="accent"
            @click="removeRecurringActions"
            >Remove</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showRemoveAllExercisesDialog"
      persistent
      max-width="400"
      @click:outside="onClickOutside"
      @keydown="onKeydown"
    >
      <v-card>
        <v-card-title class="headline">{{
          tenantTexts.participantRoutines.removeAllActivities.title
        }}</v-card-title>
        <v-card-text>
          This operation is irreversible.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="rounded-pill" text @click="cancelRemoval">Cancel</v-btn>
          <v-btn
            class="rounded-pill elevation-0"
            color="accent"
            @click="removeAllDayExercises"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <bottom-right-info-dialog
      :show-dialog="infoDialog"
      title="Hint"
      @submit="closeInfoDialog"
    >
      <p class="mb-1">You can drag single exercise between days.</p>
      <p class="mb-1">Click on exercise to select it.</p>
      <p class="mb-1">Drag to select items.</p>
      <p class="mb-1">
        Hold {{ isApple ? '⌘ CMD' : 'CTRL' }} key and click to add a item to
        selection.
      </p>
      <p class="mb-1">
        Hold {{ isApple ? '⌘ CMD' : 'CTRL' }} key and drag to add multiple items
        to selection.
      </p>
    </bottom-right-info-dialog>
    <div :class="`pa-4 multi-select-actions active`">
      <template
        v-if="copiedExercises.length > 0 || copiedExercise || copiedExerciseDay"
      >
        <v-btn
          text
          color="bhGray"
          class="transparent mr-4"
          rounded
          @click="clearCopy"
        >
          Exit copy
        </v-btn>
        <v-btn color="accent" rounded @click="pasteExercises">
          Paste
        </v-btn>
      </template>
      <template
        v-if="
          !(copiedExercises.length > 0 || copiedExercise || copiedExerciseDay)
        "
      >
        <v-btn
          class="transparent mr-4"
          text
          color="bhGray"
          rounded
          @click="deselectAllExercises"
          v-if="selectedExercises.length"
        >
          Deselect all
        </v-btn>
        <v-btn
          class="transparent mr-4"
          text
          color="bhRed"
          rounded
          @click="prepareRemoveExercises"
          v-if="selectedExercises.length"
        >
          Delete
        </v-btn>
        <v-btn
          color="accent"
          rounded
          class="mr-4"
          @click="prepareCopyExercises"
          v-if="selectedExercises.length"
        >
          Copy
        </v-btn>
        <v-btn color="accent" fab rounded @click="openInfoDialog">?</v-btn>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import Calendar from '@/components/journal/Calendar.vue'
import SequenceExerciseDialog from '@/components/dialogs/SequenceExerciseDialog.vue'
import ExerciseDefinitionDialog from '@/components/dialogs/ExerciseDefinitionDialog.vue'
import SequenceParticipantApplyDialog from '@/components/dialogs/SequenceParticipantApplyDialog.vue'
import PreviewZoomDialog from '@/components/dialogs/PreviewZoomDialog.vue'
import ParticipantExercisesContainer from '@/components/patients/ParticipantExercisesContainer.vue'
import PreviewExerciseDialog from '@/components/dialogs/PreviewExerciseDialog.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'
import { debounce } from '@/helpers/debounce'
import BottomRightInfoDialog from '@/components/dialogs/BottomRightInfoDialog.vue'
import { isApple } from '@/helpers/isApple'

// @ is an alias to /src
export default {
  name: 'PatientExercises',
  components: {
    Calendar,
    PreviewZoomDialog,
    ParticipantExercisesContainer,
    SequenceExerciseDialog,
    ExerciseDefinitionDialog,
    PreviewExerciseDialog,
    SequenceParticipantApplyDialog,
    BottomRightInfoDialog,
  },
  mixins: [TenantTextsMixin],
  data: () => ({
    today: new Date(),
    selectedDate: {
      year: '',
      month: '',
      day: '',
    },
    ready: false,
    value: '',
    events: [],
    exerciseDialogKey: 0,
    newExerciseDialogKey: 999,
    sequenceDialogKey: 1999,
    showNewExerciseDialog: false,
    showRemoveAllExercisesDialog: false,
    showEditExerciseDialog: false,
    showExercisePreviewDialog: false,
    showSequencesDialog: false,
    dialogType: 0,
    imageSrc: false,
    imageName: null,
    dialogFile: null,
    dialogFileMlUrl: null,
    dragWeekHoverTimerLeft: 0,
    dragWeekHoverTimerRight: 0,
    dragWeekHover: false,
    dragWeekAreaOpen: false,
    dragWeekHoverTimerReset: false,
    exerciseResource: null,
    exerciseResourceAdditional: null,
    exerciseDay: null,
    exerciseDefaultParams: {
      folder: 'All',
      pageSize: 100,
    },
    quickchangesQueue: [],
    quickEditPending: false,
    quickEditProgress: false,
    quickEditProgressTimeout: false,
    quickEditProgressInterval: false,
    quickEditProgressValue: 0,
    copiedExercise: null,
    copiedExercises: [],
    copiedExerciseDay: null,
    selectedExercises: [],
    infoDialog: false,
    dragging: false,
    selectedPasteDays: [],
    exerciseToRemove: null,
    removePrompt: false,
    removePromptOption: 'none',
  }),
  computed: {
    ...mapState('patientsStates', [
      'currentPatient',
      'exercises',
      'exercisesCalendar',
      'exercise',
      'columnLayoutActive',
    ]),
    ...mapState('exercisesStates', {
      exerciseDefinitions: 'exerciseDefinitions',
      dialogExerciseDefinitions: 'dialogExerciseDefinitions',
      exerciseDefinition: 'exerciseDefinition',
      sequenceLoading: 'loading',
      sequenceDefinitions: 'sequenceDefinitions',
      sequenceDefinitionsPagination: 'pagination',
    }),
    ...mapState('resourcesStates', ['resource']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
    ]),
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    isApple,
    cal() {
      return this.ready ? this.$refs.calendar : null
    },
    nowY() {
      return this.cal ? this.cal.timeToY(this.cal.times.now) + 'px' : '-10px'
    },
    dateFormattedAPI() {
      let tmpDate = {
        year: this.selectedDate.year,
        month: this.selectedDate.month - 1,
        day: this.selectedDate.day,
      }
      let date = this.$moment(tmpDate)
      return date.format('YYYY-MM-DD')
    },
    monthsStart() {
      return this.$moment(this.dateFormattedAPI)
        .startOf('month')
        .format('YYYY-MM-DD')
    },
    monthsEnd() {
      return this.$moment(this.dateFormattedAPI)
        .endOf('month')
        .format('YYYY-MM-DD')
    },
    weekStart() {
      return this.$moment(this.dateFormattedAPI)
        .startOf('week')
        .format('YYYY-MM-DD')
    },
    weekStartExtended() {
      // we need to pull more days for messages to detect if there were additional messages in past week or are upcoming in the next one
      return this.$moment(this.dateFormattedAPI)
        .startOf('week')
        .subtract(7, 'days')
        .format('YYYY-MM-DD')
    },
    weekEnd() {
      return this.$moment(this.dateFormattedAPI)
        .endOf('week')
        .format('YYYY-MM-DD')
    },
    weekEndExtended() {
      return this.$moment(this.dateFormattedAPI)
        .endOf('week')
        .add(7, 'days')
        .format('YYYY-MM-DD')
    },
    profileId() {
      return this.currentPatient.profileId
    },
    procedureDate() {
      if (this.currentPatient.procedureDate)
        return this.$moment(this.currentPatient.procedureDate).format(
          'YYYY-MM-DD',
        )
      else return false
    },
    isRoleAllowed() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] !==
        this.dictionaryBhUsersRoleIds.Consultant
      )
    },
    isPrevWeekAllowed() {
      return this.$moment(this.dateFormattedAPI)
        .subtract(7, 'days')
        .isAfter(this.$moment(this.today).startOf('week'))
    },
    isNextWeekAllowed() {
      return this.$moment(this.dateFormattedAPI)
        .add(7, 'days')
        .isAfter(this.$moment(this.today).startOf('week'))
    },
    exerciseSingleDefinitions() {
      let arr = []
      this.dialogExerciseDefinitions.forEach(element => {
        if (element.type == 'EXERCISE') {
          let obj = {}
          for (const [key, value] of Object.entries(element.exercise)) {
            obj[key] = value
          }
          arr.push(obj)
        }
      })
      return arr
    },
  },
  mounted() {
    this.ready = true
    if (this.profileId) this.loadExercises()
  },
  created() {
    this.selectedDate = {
      year: this.today.getFullYear(),
      month: this.today.getMonth() + 1,
      day: this.today.getDate(),
    }
  },
  watch: {
    profileId(value) {
      if (value) {
        this.loadExercises()
      }
    },
    $route(to, from) {
      // get new current patient data when patient id changed
      if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
        this.getPatient(to.params.id)
      }
    },
  },
  beforeRouteLeave: function(to, from, next) {
    this.stopQuickEdit()
    next()
  },
  methods: {
    ...mapActions('patientsStates', [
      'getExercises',
      'getExercisesCalendar',
      'getExercise',
      'addParticipantExercise',
      'deleteParticipantExercise',
      'deleteParticipantExercises',
      'changeParticipantExercise',
      'moveParticipantExercise',
      'addParticipantExerciseDefinition',
      'duplicateParticipantExercise',
      'pasteParticipantExercise',
      'pasteParticipantExercises',
      'pasteParticipantExercisesDay',
      'removeAllParticipantDayExercises',
      'getPatient',
    ]),
    ...mapActions('resourcesStates', [
      'getResource',
      'getResourceByUrl',
      'getResourceById',
    ]),
    ...mapActions('exercisesStates', [
      'getExerciseDefinition',
      'getExerciseDefinitions',
      'getSequenceDefinitions',
      'getDialogExerciseDefinitions',
      'assignSequence',
    ]),
    ...mapMutations('patientsStates', ['toggleColumnLayout', 'setExercise']),
    ...mapMutations('appStates', ['setSnackbarNotification']),
    ...mapMutations('exercisesStates', ['setExerciseDefinitions']),
    onDragExerciseStart() {
      this.dragging = true
    },
    onDragExerciseEnd() {
      this.dragging = false
    },
    openInfoDialog() {
      this.infoDialog = true
    },
    closeInfoDialog() {
      this.infoDialog = false
    },
    deselectAllExercises() {
      this.$refs.participantExercisesContainer.deselectAllExercises()
    },
    formatEventTime(date) {
      // calendar is returning time in eventParsed without AM/PM and moment.js can't fix it, so this function returns time in correct AM/PM format
      return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    },
    getStartingTime() {
      return this.cal ? 7 * 60 : 0 // setting 7 AM as default, can be changed into current time with this.cal.times.now.hour * 60 + this.cal.times.now.minute
    },
    getParticipantTime() {
      const currentTime = this.today.getTime()
      const currentDateFormat = this.$moment(currentTime).format(
        'YYYY-MM-DD HH:mm:ss',
      )
      const patientDateFormat = this.$moment(currentTime)
        .tz(this.currentPatient.tz)
        .format('YYYY-MM-DD HH:mm:ss')
      const calculateDiff = this.$moment(patientDateFormat).diff(
        this.$moment(currentDateFormat),
      )

      return new Date(currentTime + calculateDiff).getTime()
    },
    getCurrentTime() {
      return this.cal
        ? this.cal.times.now.hour * 60 + this.cal.times.now.minute
        : 0
    },
    loadExercises() {
      if (this.ready) {
        this.getExercises({
          participantId: this.currentPatient.id,
          params: { startDate: this.weekStart, endDate: this.weekEnd },
        }).then(x => {})
        this.getExercisesCalendar({
          participantId: this.currentPatient.id,
          params: { startDate: this.monthsStart, endDate: this.monthsEnd },
        }).then(x => {})
      }
    },
    loadDialogExerciseDefinitions(params) {
      let defaultParams = {
        type: 'EXERCISE',
        page: 0,
        pageSize: 50,
        sortBy: 'creationDate',
      }
      this.getDialogExerciseDefinitions({
        params: { ...defaultParams, ...params },
      })
    },
    searchClear() {
      this.getExerciseDefinitions({ params: { q: null } })
    },
    searchInput({ key, value }) {
      this.getExerciseDefinitions({ params: { [key]: value } })
    },
    searchMultiple(list) {
      let query = { ...this.exerciseDefaultParams }
      list.forEach(item => {
        query = { ...query, [item.key]: item.value }
      })
      this.getExerciseDefinitions({ params: query })
    },
    searchRun(search) {
      this.getExerciseDefinitions({ params: { q: search } })
    },
    reloadExerciseDefinitions(data = new URLSearchParams()) {
      const availableFilters = [
        'page',
        'pageSize',
        'sortBy',
        'sortDirection',
        'type',
      ]

      let filters = {}
      data.forEach((value, key) => {
        if (availableFilters.includes(key)) {
          filters[key] = value
        }
      })

      this.getExerciseDefinitions({ params: { ...filters, folder: 'All' } })
    },
    reloadSequenceDefinitions(data = new URLSearchParams()) {
      const availableFilters = [
        'page',
        'pageSize',
        'folderStrategy',
        'folderSharedStrategy',
        'folderId',
        'query',
        'routinesCountFrom',
        'routinesCountTo',
        'daysFrom',
        'daysTo',
      ]

      let filters = {}
      data.forEach((value, key) => {
        if (availableFilters.includes(key)) {
          filters[key] = value
        }
      })

      this.getSequenceDefinitions({ ...filters, folder: 'All' })
    },
    closeDialog() {
      this.exerciseResource = null
      this.exerciseResourceAdditional = null
      this.showNewExerciseDialog = false
      this.showEditExerciseDialog = false
      this.clearExercises()
      this.showSequencesDialog = false
    },
    clearExercises() {
      this.setExerciseDefinitions([])
    },
    prepareNewExercise(data) {
      this.addParticipantExerciseDefinition({
        participantId: this.currentPatient.id,
        data: data,
      }).then(() => {
        this.loadExercises()
      })
    },
    addExistingExercise(exerciseId, type, date) {
      this.addParticipantExercise({
        participantId: this.currentPatient.id,
        exerciseDefinitionId: exerciseId,
        date: date,
        type: type,
      }).then(() => {
        this.loadExercises()
      })
    },
    prepareEditExercise(data) {
      this.changeParticipantExercise({
        participantId: this.currentPatient.id,
        type: data.type,
        exerciseId: data.id,
        data: data,
      }).then(() => {
        this.loadExercises()
        this.closeDialog()
      })
    },
    copySequenceExercise(data) {},
    prepareMoveExercise(id, data) {
      this.moveParticipantExercise({
        participantId: this.currentPatient.id,
        exerciseId: id,
        data: data,
      }).then(() => {
        this.loadExercises()
      })
    },
    showRemovePrompt(id, type, inSeries) {
      this.exerciseToRemove = { id, type, inSeries }
      this.removePrompt = true
    },
    closeRemovePrompt() {
      this.exerciseToRemove = null
      this.removePrompt = false
      this.removePromptOption = 'none'
    },
    removeRecurringActions() {
      this.prepareRemoveExercise(
        this.exerciseToRemove.id,
        this.exerciseToRemove.type,
        this.exerciseToRemove.inSeries,
        this.removePromptOption,
      )
    },
    prepareRemoveExercise(id, type, inSeries = false, inSeriesType = false) {
      if (inSeries && inSeriesType === false) {
        this.showRemovePrompt(id, type, inSeries)
        return
      }
      this.closeRemovePrompt()
      this.deleteParticipantExercise({
        participantId: this.currentPatient.id,
        type: type,
        exerciseId: id,
        inSeriesType,
      }).then(() => {
        this.deselectAllExercises()
        this.loadExercises()
      })
    },
    async prepareRemoveExercises() {
      const exercisesToRemove = [...this.selectedExercises]
      if (exercisesToRemove.length === 1) {
        this.prepareRemoveExercise(
          exercisesToRemove[0].id,
          exercisesToRemove[0].type,
          exercisesToRemove[0].inSeries,
        )
        return
      }
      await this.deleteParticipantExercises({
        participantId: this.currentPatient.id,
        exercises: [...this.selectedExercises].map(el => el.calendarId),
      })
      this.deselectAllExercises()
    },
    prepareDuplicateExercise(itemId, type) {
      this.duplicateParticipantExercise({
        participantId: this.currentPatient.id,
        exerciseId: itemId,
        type: type,
      }).then(() => {
        this.loadExercises()
      })
    },
    selectPasteDay(date) {
      if (this.selectedPasteDays.includes(date)) {
        this.selectedPasteDays = this.selectedPasteDays.filter(
          day => day !== date,
        )
      } else {
        this.selectedPasteDays.push(date)
      }
    },
    prepareCopyExercise(itemId, type, calendarId) {
      this.copiedExercise = { id: itemId, type: type, itemId: calendarId }
    },
    prepareCopyExercises() {
      this.copiedExercises = [...this.selectedExercises].map(
        el => el.calendarId,
      )
    },
    preparePasteExercise(date) {
      this.pasteParticipantExercise({
        participantId: this.currentPatient.id,
        exerciseId: this.copiedExercise.id,
        type: this.copiedExercise.type,
        date: date,
      }).then(() => {
        this.copiedExercise = null
        this.loadExercises()
      })
    },
    prepareCopyExerciseDay(copyDate) {
      this.copiedExerciseDay = copyDate
    },
    async pasteExercises() {
      try {
        if (this.copiedExercise) {
          await this.pasteParticipantExercises({
            participantId: this.currentPatient.id,
            exercises: [this.copiedExercise.itemId],
            dates: [...this.selectedPasteDays],
          })
        } else if (this.copiedExerciseDay) {
          // if copy just one exercise from more menu
          await this.pasteParticipantExercisesDay({
            participantId: this.currentPatient.id,
            copyDate: this.copiedExerciseDay,
            pasteDates: [...this.selectedPasteDays],
          })
        } else if (this.copiedExercises.length > 0) {
          // if selected exercises (copy one or more exercises)
          await this.pasteParticipantExercises({
            participantId: this.currentPatient.id,
            exercises: [...this.copiedExercises],
            dates: [...this.selectedPasteDays],
          })
        }
        this.clearCopy()
        this.loadExercises()
      } catch (error) {
        // do nothing
      }
    },
    preparePasteExerciseDay(date) {
      this.pasteParticipantExercisesDay({
        participantId: this.currentPatient.id,
        copyDate: this.copiedExerciseDay,
        pasteDate: date,
      }).then(() => {
        this.copiedExerciseDay = null
        this.loadExercises()
      })
    },
    prepareQuickEdit(exercise, data) {
      this.quickEditPending = true
      this.quickEditProgress = true
      this.quickEditProgressStart()
      let changedData = {}
      switch (exercise.type) {
        case 'EXERCISE':
          changedData = { ...exercise.exercise }
          break
        case 'ACTIVITY':
          changedData = { ...exercise.activity }
          break
        case 'SERIES':
          changedData = { ...exercise.series }
          break
        default:
          break
      }
      changedData.type = exercise.type

      let ind = this.quickchangesQueue.findIndex(item => {
        if (item.id == changedData.id && item.type == changedData.type)
          return item
      })
      if (ind > -1) this.quickchangesQueue[ind][data.key] = data.value
      else {
        changedData[data.key] = data.value
        this.quickchangesQueue.push(changedData)
      }

      debounce(
        () => {
          this.quickchangesQueue.forEach(item => {
            this.prepareEditExercise(item, null, null)
          })
          this.quickchangesQueue = []
          this.quickEditPending = false
        },
        3000,
        'QuickEdit',
      )
    },
    stopQuickEdit() {
      clearTimeout(this.quickEditProgressTimeout)
      clearInterval(this.quickEditProgressInterval)
      this.quickEditProgressValue = 0
      this.quickchangesQueue = []
      this.quickEditPending = false
      this.quickEditProgress = false
      debounce(() => {}, 3000, 'QuickEdit', 'cancel')
    },
    prepareSeriesQuickEdit(data) {
      this.prepareQuickEdit({ type: 'EXERCISE', exercise: data.exercise }, data)
    },
    quickEditProgressStart() {
      clearTimeout(this.quickEditProgressTimeout)

      if (this.quickEditProgressInterval)
        clearInterval(this.quickEditProgressInterval)
      this.quickEditProgressValue = 0
      this.quickEditProgressInterval = setInterval(() => {
        if (this.quickEditProgressValue > 100) {
          clearInterval(this.quickEditProgressInterval)
          return
        }
        this.quickEditProgressValue += 25
      }, 540)
      if (this.quickEditProgressTimeout)
        clearTimeout(this.quickEditProgressTimeout)
      this.quickEditProgressTimeout = setTimeout(() => {
        this.quickEditProgress = false
        this.quickEditProgressValue = 0
        clearInterval(this.quickEditProgressInterval)
      }, 4000)
    },
    applySequence(sequenceId, date) {
      let data = {
        startDate: date,
        participants: [
          {
            participantId: this.currentPatient.id,
          },
        ],
      }
      this.assignSequence({ sequenceId: sequenceId, data: data }).then(() => {
        this.loadExercises()
        this.sequenceDialogKey++
      })
    },
    openNewExerciseDialog(day) {
      if (day) this.exerciseDay = day
      this.showNewExerciseDialog = true
    },
    openRemoveAllExercisesDialog(day) {
      if (day) this.exerciseDay = day
      this.showRemoveAllExercisesDialog = true
    },
    async openEditExerciseDialog({ id, type, day }) {
      if (day) this.exerciseDay = day
      await this.getExercise({
        participantId: this.currentPatient.id,
        type: type,
        exerciseId: id,
      })
      this.showEditExerciseDialog = true
      if (type == 'SERIES') this.reloadExerciseDefinitions()
    },
    clearExerciseResource() {
      this.exerciseResource = null
    },
    clearExerciseResourceAdditional() {
      this.exerciseResourceAdditional = null
    },
    selectDate(newDate) {
      this.selectedDate = { ...newDate }
    },
    editChangeExercisesDefinition(data) {
      this.changeExercisesMessagesDefinition({
        id: data.id,
        data: {
          from: data.from,
          to: data.to,
          time: data.time,
          message: data.message,
          isProcedureDateRelated: data.isProcedureDateRelated,
          resourceId: data.resourceId,
          daysOfWeek: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true,
          },
        },
        params: {
          id: this.profileId,
          from: this.weekStartExtended,
          to: this.weekEndExtended,
        },
      }).then(x => {
        //  // do we need to do anything here?
      })
    },
    reloadResource(id) {
      this.getResource({ id: id })
    },
    reloadExerciseResource(id, type) {
      this.prepareExerciseResource(id, type)
    },
    prepareExerciseResource(id, resourceType) {
      if (!id) {
        if (resourceType == 'additionalMedia') {
          this.exerciseResourceAdditional = null
        } else {
          this.exerciseResource = null
        }
      } else {
        this.getResourceById({ resourceId: id })
          .then(response => {
            const { data } = response
            let type = 2
            switch (data.fileTypeId) {
              case 18:
                type = 2
                break
              case 19:
                type = 4
                break
              default:
                type = 2
                break
            }
            if (resourceType == 'additionalMedia') {
              if (data.fileUrl) {
                this.exerciseResourceAdditional = {
                  id: id,
                  previewUrl: data.previewUrl,
                  thumbnailUrl: data.thumbnailUrl,
                  name: data.name,
                  title: data.title,
                  dialogFileMlUrl: data.fileUrl,
                  fileUrl: data.fileUrl,
                  fileTypeId: data.fileTypeId,
                }
              } else {
                this.exerciseResourceAdditional = null
              }
            } else {
              if (data.fileUrl) {
                this.exerciseResource = {
                  id: id,
                  previewUrl: data.previewUrl,
                  thumbnailUrl: data.thumbnailUrl,
                  name: data.name,
                  title: data.title,
                  dialogFileMlUrl: data.fileUrl,
                  fileUrl: data.fileUrl,
                  fileTypeId: data.fileTypeId,
                }
              } else {
                this.exerciseResource = null
              }
            }
          })
          .catch(error => {
            console.log(error)
          })
      }
    },
    showBiggerImage({ url, name, file, mlResourceUrl = null, type = 1 }) {
      this.imageSrc = url
      this.imageName = name
      this.dialogFile = file
      this.dialogFileMlUrl = mlResourceUrl
      this.dialogType = type
    },
    closeMediaDialog() {
      this.dialogType = 0
      this.currentFile = null
      this.imageSrc = null
      this.imageName = null
      this.dialogFileMlUrl = null
    },
    nextWeek() {
      this.$nextTick(() => {
        let tmpDate = {
          year: this.selectedDate.year,
          month: this.selectedDate.month - 1,
          day: this.selectedDate.day,
        }
        let date = this.$moment(tmpDate).add(7, 'days')
        let tmpDate2 = {
          year: date.year(),
          month: date.month() + 1, // month returns 0-11, selectedDate uses 1-12
          day: date.date(),
        }
        this.selectedDate = { ...tmpDate2 }
      })
    },
    nextWeekT() {
      if (this.dragging) {
        this.dragWeekHover = true
        this.dragWeekHoverTimerRight = 100
        debounce(
          () => {
            if (this.dragWeekHover) this.nextWeek()
          },
          1100,
          'NextWeek',
        )
      }
    },
    prevWeek() {
      this.$nextTick(() => {
        let tmpDate = {
          year: this.selectedDate.year,
          month: this.selectedDate.month - 1,
          day: this.selectedDate.day,
        }
        let date = this.$moment(tmpDate).subtract(7, 'days')
        let tmpDate2 = {
          year: date.year(),
          month: date.month() + 1, // month returns 0-11, selectedDate uses 1-12
          day: date.date(),
        }
        this.selectedDate = { ...tmpDate2 }
      })
    },
    prevWeekT() {
      if (this.dragging) {
        this.dragWeekHover = true
        this.dragWeekHoverTimerLeft = 100
        debounce(
          () => {
            if (this.dragWeekHover && this.dragging) this.prevWeek()
          },
          1100,
          'PrevWeek',
        )
      }
    },
    resetDragTimers() {
      this.dragWeekHoverTimerReset = true
      this.dragWeekHoverTimerLeft = 0
      this.dragWeekHoverTimerRight = 0
      setTimeout(() => {
        this.dragWeekHoverTimerReset = false
      }, 10)
    },
    openCreateNewExerciseDialog() {
      this.setExercise(null)
      this.exerciseResource = null
      this.exerciseResourceAdditional = null
      this.showEditExerciseDialog = true
    },
    createNewExerciseDefinition(data) {
      data.date = this.exerciseDay
      this.addParticipantExerciseDefinition({
        participantId: this.currentPatient.id,
        data: data,
      }).then(() => {
        this.closeDialog()
      })
    },
    async exerciseGetPreview(exercise) {
      let id = exercise.split('__')[1]
      let type = exercise.split('__')[0].toUpperCase()
      await this.getExerciseDefinition({ id: id, type: type })
      this.showExercisePreviewDialog = true
    },
    openMediaPreview(media) {
      this.showBiggerImage({
        url: media.mediaUrl,
        file: media.mediaUrl,
        name: media.filename,
        mlResourceUrl: null,
        type: media.fileTypeId == 19 ? 3 : 1,
      })
    },
    removeAllDayExercises() {
      this.removeAllParticipantDayExercises({
        participantId: this.currentPatient.id,
        day: this.exerciseDay,
      }).then(() => {
        this.showRemoveAllExercisesDialog = false
        this.loadExercises()
      })
    },
    cancelRemoval() {
      this.showRemoveAllExercisesDialog = false
      this.exerciseDay = null
      this.closeRemovePrompt()
    },
    onClickOutside() {
      this.cancelRemoval()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.cancelRemoval()
    },
    clearCopy() {
      this.copiedExercise = null
      this.copiedExercises = []
      this.copiedExerciseDay = null
      this.selectedPasteDays = []
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.v-event-draggable {
  padding-left: 6px;
}

.v-event-timed {
  user-select: none;
  -webkit-user-select: none;
}

.patient-exercises {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 10px;
  height: 100%;
  &--wrap {
    height: 100%;
    position: relative;
    z-index: 3;
  }
  &--not-found {
    h2 {
      margin-top: 30px;
    }
  }
  & > .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 0;
    flex-grow: 1;
    overflow: hidden;
    & > .row > .col {
      padding-bottom: 0;
      display: flex;
      flex-direction: column;
    }
  }
  &__v-sheet {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
  .template-button {
    .v-icon {
      margin-left: 10px;
    }
  }
  &-tz {
    color: $bh-gray--2;
    margin-left: 10px;
    flex-grow: 1;
  }
  .v-calendar-daily {
    max-height: calc(100vh - 232px);
    .v-calendar-daily {
      &__day {
        border-right: 0 !important;
        border-bottom: 0 !important;
        &-interval {
          border-top: 0 !important;
        }
      }
      &_head {
        &-day {
          border-right: 0 !important;
          border-bottom: 0 !important;
          display: flex;
          justify-content: flex-end;
          flex-direction: row-reverse;
          position: relative;
          padding: 19px 9px;
          &::after {
            position: absolute;
            content: '';
            bottom: 9px;
            right: 9px;
            left: 9px;
            border-top: 1px solid $bh-gray--5;
          }
          &.v-present {
            .v-calendar-daily_head-day-label {
              color: $bh-blue;
            }
            &::after {
              border-color: $bh-blue;
            }
          }
          &.v-past {
            .v-calendar-daily_head-day-label {
              color: $bh-black;
            }
            .v-calendar-daily_head-weekday {
              color: $bh-black;
            }
          }
          .v-event {
            margin-left: 10px;
          }
        }
      }
      &__intervals {
        &-head {
          border-right: 0 !important;
        }
        &-body {
          border-right: 0 !important;
        }
      }
    }
    .v-present {
      background: #f7f9ff;
    }
  }
  .v-calendar {
    transition-duration: 300ms;
    &.dragBorder {
      box-shadow: 0px 0px 10px $bh-red--dark;
    }
    &-daily {
      border-top: 0 !important;
      border-left: 0 !important;
      &__head {
      }
      &_head {
        &-weekday {
          font-size: 14px;
          line-height: 20px;
          padding: 0 0 0 4px;
          text-transform: capitalize;
          order: 1;
        }
        &-day-label {
          font-size: 14px;
          font-weight: bold;
          padding: 0;
          line-height: 20px;
          order: 2;
        }
        &-day {
          &.v-present {
            .v-calendar-daily_head-weekday {
              color: $bh-blue;
            }
          }
          &.v-past {
            .v-calendar-daily_head-weekday {
              color: $bh-gray--2 !important;
            }
            .v-calendar-daily_head-day-label {
              color: $bh-gray--2 !important;
            }
          }
        }
      }
      &__scroll-area {
        overflow: auto;
      }
      &__intervals-head {
        &::after {
          bottom: 9px;
        }
      }
    }
    &-events,
    &-events.theme--light {
      .v-event-timed {
        border: 0 !important;
        padding: 10px;
        font-size: 12px;
        min-height: 80px !important;
        margin-left: 5px;
        z-index: initial;
        overflow: initial;
        &-container {
          z-index: 1;
        }
        .v-event-draggable {
          display: flex;
          height: 100%;
          flex-direction: column;
          justify-content: space-between;
          z-index: 1;
          .v-event-name {
            font-weight: bold;
            overflow: hidden;
            font-size: 16px;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .v-event-time {
            opacity: 0.8;
            font-weight: bold;
          }
        }
        .series-indicator {
          position: absolute;
          background: $bh-blue--2;
          opacity: 0.15;
          top: -8px;
          left: -5px;
          right: -5px;
          bottom: -8px;
          z-index: -1;
          &.start {
            border-radius: 50px 0 0 50px;
            left: 10px;
          }
          &.startend {
            display: none;
          }
          &.end {
            border-radius: 0 50px 50px 0;
            right: 10px;
          }
        }
        .status {
          position: absolute;
          display: block;
          width: 16px;
          height: 16px;
          top: 5px;
          right: 5px;
          border: 2px solid $bh-white;
          border-radius: 50%;
          &.sent {
            &::after {
              content: '';
              display: block;
              width: 8px;
              height: 8px;
              position: absolute;
              top: 2px;
              right: 2px;
              background: $bh-white;
              border-radius: 50%;
            }
          }
          &.error {
            &::after {
              content: '✕';
              display: block;
              width: 12px;
              font-size: 14px;
              height: 14px;
              line-height: 14px;
              text-align: center;
              color: $bh-white;
              position: absolute;
              top: 0px;
            }
          }
        }
        .series-drag {
          position: absolute;
          left: -32px;
          top: 0;
          height: 100%;
          .v-btn {
            height: 100%;
            border-radius: 50% 0 0 50% !important;
            background: rgba(255, 255, 255, 0.5);
          }
        }
      }
    }
  }
  .v-event-timed-container {
    z-index: 1;
  }
  .procedure-date {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: $bh-red;
    opacity: 0.2;
    z-index: 0;
  }
  .navbar {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }
  .drag-over {
    position: absolute;
    top: 248px;
    width: 75px;
    bottom: 0;
    background: $bh-blue--2;
    opacity: 0;
    z-index: 99;
    display: flex;
    justify-content: center;
    align-items: center;
    transition-duration: 300ms;
    cursor: pointer;
    &.left {
      left: 22px;
      padding-right: 10px;
      background: rgb(0, 0, 0);
      background: -moz-linear-gradient(
        270deg,
        rgba(0, 0, 0, 0) 0%,
        $bh-blue--2 25%
      );
      background: -webkit-linear-gradient(
        270deg,
        rgba(0, 0, 0, 0) 0%,
        $bh-blue--2 25%
      );
      background: linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, $bh-blue--2 25%);
    }
    &.right {
      right: 22px;
      padding-left: 10px;
      background: rgb(0, 0, 0);
      background: -moz-linear-gradient(
        90deg,
        rgba(0, 0, 0, 0) 0%,
        $bh-blue--2 25%
      );
      background: -webkit-linear-gradient(
        90deg,
        rgba(0, 0, 0, 0) 0%,
        $bh-blue--2 25%
      );
      background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, $bh-blue--2 25%);
    }
    &.visible {
      opacity: 0.4;
      z-index: 99;
    }
  }
  .v-progress-circular__overlay {
    transition: all 0.5s linear;
  }
  .v-progress-circular.reset {
    .v-progress-circular__overlay {
      transition: all 0.01s linear !important;
    }
  }
  &__quick-edit-progress {
    span {
      color: $bh-blue;
      font-weight: bold;
    }
  }

  &--wrap {
    .multi-select-actions {
      position: fixed;
      bottom: 0;
      right: 0;
      display: none;
      align-items: center;
      justify-content: flex-end;
      z-index: 100;

      &.active {
        display: flex;
      }
    }
  }
}
</style>
